/*
 * dunkleycustom — "Notify me when back in stock" (Klaviyo client-side back-in-stock)
 *
 * Front-end only. For each [data-luma-notify] block (rendered by
 * snippets/luma-notify-me.liquid) this:
 *   1. Shows/hides the trigger as the shopper switches variants, following the
 *      theme's `variant:change:<sectionId>:<productId>` event (detail.variant
 *      carries .id and .available). Only shown when the variant is sold out.
 *   2. Runs a small centred modal (open/close, Esc, backdrop click, focus trap).
 *   3. POSTs a Klaviyo client-side back-in-stock subscription on submit.
 *
 * The Klaviyo public API key (Site ID) and API revision are read from data
 * attributes on the block — no key is hard-coded in this file. Endpoint + payload
 * verified against Klaviyo docs: POST /client/back-in-stock-subscriptions/,
 * revision 2026-04-15, HTTP 202 = accepted, public key only (no auth header).
 */
(function () {
  if (window.__lumaNotifyLoaded) return;
  window.__lumaNotifyLoaded = true;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var VARIANT_CHANGE = 'variant:change'; // matches util.events.js EVENTS.variantChange

  function init() {
    var blocks = document.querySelectorAll('[data-luma-notify]');
    Array.prototype.forEach.call(blocks, setup);
  }

  function setup(root) {
    if (root.__lumaNotifyReady) return;
    root.__lumaNotifyReady = true;

    var sectionId = root.getAttribute('data-section-id');
    var productId = root.getAttribute('data-product-id');
    var companyId = root.getAttribute('data-company-id');
    var revision = root.getAttribute('data-revision');
    var currentVariantId = root.getAttribute('data-variant-id');

    var openBtn = root.querySelector('[data-luma-notify-open]');
    var modal = root.querySelector('[data-luma-notify-modal]');
    var form = root.querySelector('.luma-notify__form');
    var emailInput = root.querySelector('.luma-notify__email');
    var errorEl = root.querySelector('[data-luma-notify-error]');
    var body = root.querySelector('.luma-notify__body');
    var submitBtn = form ? form.querySelector('.luma-notify__submit') : null;
    // Button text lives in an inner .element-text span (theme button anatomy);
    // swap text there, never on the button itself, or the span gets destroyed.
    var submitLabel = submitBtn ? submitBtn.querySelector('.element-text') || submitBtn : null;
    var lastFocused = null;

    // Sit the trigger directly below the add-to-cart ("Sold Out") button, inside
    // the buy-buttons form's flex stack (the form's own gap handles spacing).
    // The modal stays outside the form: nesting its email form inside the
    // product form would be invalid HTML. Event handlers below bind to the
    // element reference, so the relocation is transparent to them.
    var addToCart = document.getElementById('ProductSubmitButton-' + sectionId);
    if (openBtn && addToCart) addToCart.insertAdjacentElement('afterend', openBtn);

    // --- Visibility: follow the theme's variant-change event ---
    if (sectionId && productId) {
      document.addEventListener(VARIANT_CHANGE + ':' + sectionId + ':' + productId, function (e) {
        var variant = e.detail && e.detail.variant;
        if (!variant) {
          if (openBtn) openBtn.hidden = true; // no matching variant -> nothing to subscribe to
          return;
        }
        currentVariantId = variant.id;
        if (openBtn) openBtn.hidden = variant.available !== false; // show only when sold out
      });
    }

    // --- Modal ---
    function openModal() {
      if (!modal) return;
      lastFocused = document.activeElement;
      if (errorEl) errorEl.hidden = true;
      modal.hidden = false;
      document.documentElement.classList.add('modal-open');
      document.addEventListener('keydown', onKeydown);
      if (emailInput) setTimeout(function () { emailInput.focus(); }, 30);
    }

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.classList.remove('modal-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'Tab') trapTab(e);
    }

    function trapTab(e) {
      var focusables = Array.prototype.filter.call(
        modal.querySelectorAll('button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.hidden && el.offsetParent !== null; }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    if (openBtn) openBtn.addEventListener('click', openModal);
    Array.prototype.forEach.call(root.querySelectorAll('[data-luma-notify-close]'), function (el) {
      el.addEventListener('click', closeModal);
    });

    // --- Submit -> Klaviyo client back-in-stock subscription ---
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        // Stop the submit bubbling to the theme's <block-buy-buttons> listener,
        // which would otherwise fire an errant add-to-cart on this modal form.
        e.stopPropagation();
        if (errorEl) errorEl.hidden = true;

        if (emailInput && !emailInput.checkValidity()) {
          emailInput.reportValidity();
          return;
        }

        var email = emailInput ? emailInput.value.trim() : '';
        if (!email || !currentVariantId || !companyId) {
          if (errorEl) errorEl.hidden = false;
          return;
        }

        var originalText = submitLabel ? submitLabel.textContent : '';
        if (submitBtn) { submitBtn.disabled = true; submitLabel.textContent = 'Submitting…'; }

        fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'revision': revision, 'content-type': 'application/json' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: email } } },
                channels: ['EMAIL']
              },
              relationships: {
                variant: {
                  data: { type: 'catalog-variant', id: '$shopify:::$default:::' + currentVariantId }
                }
              }
            }
          })
        }).then(function (res) {
          if (res.status === 202) {
            if (body) {
              body.innerHTML =
                '<p class="luma-notify__success">Thanks. We\'ll email you as soon as this is back in stock.</p>';
            }
          } else {
            throw new Error('Unexpected status ' + res.status);
          }
        }).catch(function () {
          if (errorEl) errorEl.hidden = false;
          if (submitBtn) { submitBtn.disabled = false; submitLabel.textContent = originalText; }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
